import { uiLocale } from '../i18n/language';

/**
 * Prompt Studio content (a prompt with its steps, a content block, a template, an evaluation
 * suite) in each language, as the API sends it: `{language: {original text: translated text}}`.
 * Filled in the background after a save, so any text may not be translated yet.
 */
export type ContentTranslations = Record<string, Record<string, string>>;

/**
 * `text` in the UI's language, or the original until its translation is ready.
 * Display only: anything sent back to the API (the prompt id, the generated prompt)
 * keeps the original, which is what the roleplay and its scoring run on.
 */
export function localizeContentText(translations: ContentTranslations | null | undefined, text: string): string {
  return translations?.[uiLocale()]?.[text] || text;
}

/** A name sent as `{language: name}` (e.g. `prompt_name_translations`), in the UI's language. */
export function localizeName(byLanguage: Record<string, string> | null | undefined, name: string): string {
  return byLanguage?.[uiLocale()] || name;
}

/**
 * `text` in the other languages, for an editor that shows the original: every translation
 * that differs from it, so an English prompt shows its Spanish and a Spanish one its English.
 * Empty until translated, and as soon as the text is edited away from what was translated.
 */
export function otherTranslations(
  translations: ContentTranslations | null | undefined,
  text: string | null | undefined,
): Array<{ language: string; text: string }> {
  const original = text?.trim();
  if (!translations || !text || !original) return [];
  return Object.entries(translations)
    .map(([language, byText]) => ({ language, text: byText[text] ?? byText[original] }))
    .filter((entry): entry is { language: string; text: string } => !!entry.text && entry.text !== original);
}
